"use client"

import * as React from "react"
import Link from "next/link"
import Image from "next/image"
import { usePathname, useRouter } from "next/navigation"
import { cn } from "@sarkiai/utils"
import { authClient } from "@djotta/auth/client"
import {
  Button,
  ThemeToggle,
  Sheet,
  SheetContent,
  SheetTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@sarkiai/ui"
import { Menu, X, Globe, LogIn, User, MessageSquare, CreditCard, Bot, Building2, ChevronDown, Briefcase, FileText, ArrowRight, TrendingUp, Coins, Palette } from "lucide-react"
import { useLanguage, useTranslations } from "@/components/I18nProvider"

interface PublicHeaderProps {
  branding?: {
    logo?: React.ReactNode
    title?: string
    homeUrl?: string
  }
}

export function PublicHeader({ branding }: PublicHeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)
  const [isScrolled, setIsScrolled] = React.useState(false)
  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null)
  const pathname = usePathname()
  const router = useRouter()
  const { locale, setLocale } = useLanguage()
  const t = useTranslations("appbar")
  const { data: session, isPending } = authClient.useSession()
  const isConnected = !isPending && !!session?.user

  const isProd = process.env.NODE_ENV === "production"
  const vcardUrl = "https://vcard.djotta.com"
  const authUrl = isProd ? `${vcardUrl}/login` : "/auth/login"
  const accountUrl = isProd ? `${vcardUrl}/account` : "/account"

  // Services dropdown items
  const services = [
    {
      id: "enterprise",
      name: t("enterprise") || "Enterprise",
      href: "/enterprise",
      icon: Building2,
      description: "Agent IA pour entreprises"
    },
    {
      id: "sarkiai",
      name: t("sarkiai") || "SarkiAI",
      href: "/sarkiai",
      icon: Bot,
      description: "Assistant IA personnel"
    },
    {
      id: "vcard",
      name: t("vcard") || "VCard",
      href: "/vcard",
      icon: CreditCard,
      description: "Cartes de visite digitales NFC"
    }
  ]

  // Use cases dropdown items
  const useCases = [
    {
      id: "stdsa",
      name: t("stdsa_case") || "STD SA",
      href: "/use-cases/stdsa",
      icon: TrendingUp,
      logo: "/assets/use-cases/stdsa/logo.png",
      image: "/assets/use-cases/stdsa/services/road-freight.jpg",
      description: "Comment STD SA a digitalisé sa relation client avec StdAI pour gérer des opérations complexes de transit."
    },
  ]

  // Careers dropdown items
  const careers = [
    {
      id: "opportunities",
      name: t("opportunities") || "Opportunités",
      href: "/careers",
      icon: Briefcase,
      description: "Rejoignez notre équipe"
    },
    {
      id: "job_openings",
      name: t("job_openings") || "Offres d'emploi",
      href: "/careers/jobs",
      icon: FileText,
      description: "Postes disponibles"
    },
  ]

  // Descriptions for mega menu columns
  const menuDescriptions: Record<string, { title: string; description: string; cta: string; ctaHref: string; logo?: string; image?: string }> = {
    services: {
      title: "Solutions innovantes",
      description: "Djotta développe des solutions technologiques pour digitaliser et automatiser votre business.",
      cta: "En savoir plus",
      ctaHref: "/services"
    },
    useCases: {
      title: "Études de cas",
      description: "Découvrez comment nos clients transforment leur business avec nos solutions.",
      cta: "Voir tous les cas",
      ctaHref: "/use-cases"
    },
    careers: {
      title: "Rejoignez-nous",
      description: "Construisez l'avenir de l'IA en Afrique avec notre équipe passionnée.",
      cta: "Voir les opportunités",
      ctaHref: "/careers"
    }
  }

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleLanguageChange = (value: string) => {
    setLocale(value)
  }

  const handleSignOut = async () => {
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          router.push("/auth/login")
        },
      },
    })
  }

  const smoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("/#")) {
      e.preventDefault()
      const id = href.split("#")[1]
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: "smooth" })
      }
      setIsMobileMenuOpen(false)
    }
  }

  // Get hovered item description
  const getHoveredDescription = (items: any[], menuKey: keyof typeof menuDescriptions) => {
    const item = items.find(i => i.id === hoveredItem)
    if (item) {
      return {
        title: item.name,
        description: item.description,
        logo: item.logo,
        image: item.image
      }
    }
    return menuDescriptions[menuKey]
  }

  return (
    <header
      className={cn(
        "z-50 w-full flex justify-center",
        "transition-[top,height,background-color,border-color,backdrop-filter,position] duration-500 ease-out",
        isScrolled
          ? "sticky top-4 h-14 px-4 lg:px-12"
          : "absolute top-0 h-16 bg-transparent"
      )}
    >
      <div
        className={cn(
          "container mx-auto flex h-full items-center justify-between px-4 md:px-6 lg:px-8",
          "transition-[border-radius,background-color,box-shadow,backdrop-filter] duration-500 ease-out",
          isScrolled
            ? "rounded-full border bg-background/80 backdrop-blur shadow-md"
            : "bg-transparent backdrop-blur-none border-none shadow-none"
        )}
      >
        {/* Logo */}
        <Link
          href={branding?.homeUrl || "/"}
          className="flex items-center gap-2 font-bold text-xl"
        >
          {branding?.logo}
          <span className={cn("inline-block transition-all", isScrolled && "text-sm")}>
            {branding?.title || "Djotta"}
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {/* Services Mega Menu */}
          <DropdownMenu onOpenChange={(open) => !open && setHoveredItem(null)}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs font-medium gap-1 hover:!bg-transparent data-[state=open]:!bg-transparent hover:underline underline-offset-4 focus-visible:ring-0 focus:!bg-transparent active:!bg-transparent hover:text-foreground"
              >
                {t("services")}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[420px] p-0 backdrop-blur-xl bg-background/95 border border-border/50 shadow-xl">
              <div className="grid grid-cols-2 gap-0">
                {/* Services List */}
                <div className="p-4 space-y-1">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <span className="font-semibold text-sm">{t("services")}</span>
                  </div>
                  {services.map((service) => (
                    <DropdownMenuItem
                      key={service.name}
                      asChild
                      className="rounded-md cursor-pointer p-0 focus:bg-accent/50"
                      onMouseEnter={() => setHoveredItem(service.id)}
                    >
                      <Link
                        href={service.href}
                        onClick={(e) => smoothScroll(e, service.href)}
                        className="flex items-center gap-3 p-2 w-full"
                      >
                        <div className="p-1.5 rounded-md bg-foreground/5">
                          <service.icon className="h-4 w-4 text-foreground" />
                        </div>
                        <div className="font-medium text-sm">{service.name}</div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </div>

                {/* Description Column */}
                <div className="p-4 bg-muted/30 flex flex-col justify-center overflow-hidden min-h-[180px]">
                  <div key={hoveredItem || "default"} className="space-y-3 animate-in fade-in slide-in-from-right-2 duration-300">
                    {getHoveredDescription(services, "services").image && (
                      <div className="relative h-20 w-full rounded-lg overflow-hidden mb-2">
                        <Image
                          src={getHoveredDescription(services, "services").image}
                          alt=""
                          fill
                          className="object-cover"
                        />
                      </div>
                    )}
                    <h4 className="font-semibold text-sm">{getHoveredDescription(services, "services").title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {getHoveredDescription(services, "services").description}
                    </p>
                    <Link
                      href={menuDescriptions.services.ctaHref}
                      className="inline-flex items-center text-xs font-medium text-primary hover:underline"
                    >
                      {menuDescriptions.services.cta} <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Use Cases Mega Menu */}
          <DropdownMenu onOpenChange={(open) => !open && setHoveredItem(null)}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs font-medium gap-1 hover:!bg-transparent data-[state=open]:!bg-transparent hover:underline underline-offset-4 focus-visible:ring-0 focus:!bg-transparent active:!bg-transparent hover:text-foreground"
              >
                {t("use_cases")}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[380px] p-0 backdrop-blur-xl bg-background/95 border border-border/50 shadow-xl">
              <div className="grid grid-cols-2 gap-0">
                <div className="p-4 space-y-1">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <span className="font-semibold text-sm">{t("use_cases")}</span>
                  </div>
                  {useCases.map((useCase) => (
                    <DropdownMenuItem
                      key={useCase.name}
                      asChild
                      className="rounded-md cursor-pointer p-0 focus:bg-accent/50"
                      onMouseEnter={() => setHoveredItem(useCase.id)}
                    >
                      <Link href={useCase.href} className="flex items-center gap-3 p-2 w-full">
                        <div className="p-1.5 rounded-md bg-foreground/5 shrink-0">
                          {useCase.logo ? (
                            <Image src={useCase.logo} alt={useCase.name} width={20} height={20} className="object-contain" />
                          ) : (
                            <useCase.icon className="h-4 w-4 text-foreground" />
                          )}
                        </div>
                        <div className="font-medium text-sm">{useCase.name}</div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </div>

                <div className="p-4 bg-muted/30 flex flex-col justify-center overflow-hidden min-h-[180px]">
                  <div key={hoveredItem || "default"} className="space-y-3 animate-in fade-in slide-in-from-right-2 duration-300">
                    {getHoveredDescription(useCases, "useCases").image && (
                      <div className="relative h-20 w-full rounded-lg overflow-hidden mb-2">
                        <Image
                          src={getHoveredDescription(useCases, "useCases").image}
                          alt=""
                          fill
                          className="object-cover"
                        />
                      </div>
                    )}
                    <h4 className="font-semibold text-sm">{getHoveredDescription(useCases, "useCases").title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {getHoveredDescription(useCases, "useCases").description}
                    </p>
                    <Link
                      href={menuDescriptions.useCases.ctaHref}
                      className="inline-flex items-center text-xs font-medium text-primary hover:underline"
                    >
                      {menuDescriptions.useCases.cta} <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Pricing Link */}
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="text-xs font-medium hover:!bg-transparent data-[state=open]:!bg-transparent hover:underline underline-offset-4 focus-visible:ring-0 focus:!bg-transparent active:!bg-transparent hover:text-foreground"
          >
            <Link href="/pricing">
              {t("pricing")}
            </Link>
          </Button>

          {/* Careers Mega Menu */}
          <DropdownMenu onOpenChange={(open) => !open && setHoveredItem(null)}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs font-medium gap-1 hover:!bg-transparent data-[state=open]:!bg-transparent hover:underline underline-offset-4 focus-visible:ring-0 focus:!bg-transparent active:!bg-transparent hover:text-foreground"
              >
                {t("careers")}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[380px] p-0 backdrop-blur-xl bg-background/95 border border-border/50 shadow-xl">
              <div className="grid grid-cols-2 gap-0">
                <div className="p-4 space-y-1">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <span className="font-semibold text-sm">{t("careers")}</span>
                  </div>
                  {careers.map((career) => (
                    <DropdownMenuItem
                      key={career.name}
                      asChild
                      className="rounded-md cursor-pointer p-0 focus:bg-accent/50"
                      onMouseEnter={() => setHoveredItem(career.id)}
                    >
                      <Link href={career.href} className="flex items-center gap-3 p-2 w-full">
                        <div className="p-1.5 rounded-md bg-foreground/5">
                          <career.icon className="h-4 w-4 text-foreground" />
                        </div>
                        <div className="font-medium text-sm">{career.name}</div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </div>

                <div className="p-4 bg-muted/30 flex flex-col justify-center overflow-hidden">
                  <div key={hoveredItem || "default"} className="space-y-3 animate-in fade-in slide-in-from-right-2 duration-300">
                    <h4 className="font-semibold text-sm">{getHoveredDescription(careers, "careers").title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {getHoveredDescription(careers, "careers").description}
                    </p>
                    <Link
                      href={menuDescriptions.careers.ctaHref}
                      className="inline-flex items-center text-xs font-medium text-primary hover:underline"
                    >
                      {menuDescriptions.careers.cta} <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Contact Link */}
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="text-xs font-medium hover:!bg-transparent data-[state=open]:!bg-transparent hover:underline underline-offset-4 focus-visible:ring-0 focus:!bg-transparent active:!bg-transparent hover:text-foreground"
          >
            <Link href="/contact">
              {t("contact")}
            </Link>
          </Button>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1">
            <TooltipProvider>
              {/* Language dropdown */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon-sm">
                        <Globe className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleLanguageChange("fr")}
                        className={cn(locale === "fr" && "bg-primary text-primary-foreground")}
                      >
                        FR
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleLanguageChange("en")}
                        className={cn(locale === "en" && "bg-primary text-primary-foreground")}
                      >
                        EN
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TooltipTrigger>
                <TooltipContent>{t("change_language")}</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <ThemeToggle className="size-8" />
                </TooltipTrigger>
                <TooltipContent>{t("dark_mode")}</TooltipContent>
              </Tooltip>

              {!isConnected ? (
                <React.Fragment>
                  <Button asChild size="sm" className="h-8 rounded-full px-4 bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black">
                    <Link href={authUrl}>
                      {/* <LogIn className="mr-2 h-4 w-4" /> */}
                      {t("login")}
                    </Link>
                  </Button>
                </React.Fragment>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" className="rounded-full border">
                          <User className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link href={accountUrl}>{t("my_account")}</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleSignOut}>
                          {t("logout")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TooltipTrigger>
                  <TooltipContent>{t("my_account")}</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon-sm"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>
      </div >

      {/* Mobile Navigation - Replaced with Sheet for better scroll lock and overlay */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent
          side="top"
          className="h-[100dvh] w-full border-none bg-background/95 backdrop-blur-xl p-0 flex flex-col animate-in fade-in slide-in-from-top duration-500"
        >
          <div className="flex items-center justify-between px-6 h-16 border-b bg-background/50">
            <Link
              href={branding?.homeUrl || "/"}
              className="flex items-center gap-2 font-bold text-xl"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {branding?.logo}
              <span>{branding?.title || "Djotta"}</span>
            </Link>
            {/* The Sheet component already provides a close button at top-right by default, 
                but we can also add a custom one if we want it specifically styled.
                The default Radix X button is usually fixed at top-4 right-4.
            */}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="container mx-auto flex flex-col gap-6">
              {/* Services section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-md bg-foreground/10">
                    <CreditCard className="h-4 w-4 text-foreground" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("services")}</span>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {services.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted transition-colors border border-transparent hover:border-border/50"
                      onClick={(e) => {
                        smoothScroll(e, item.href)
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      <div className="p-2 rounded-lg bg-foreground/5 shrink-0">
                        <item.icon className="h-5 w-5 text-foreground" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{item.name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{item.description}</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
                    </Link>
                  ))}
                </div>
              </div>

              <hr className="border-border/50" />

              {/* Use Cases section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-md bg-foreground/10">
                    <TrendingUp className="h-4 w-4 text-foreground" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("use_cases")}</span>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {useCases.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted transition-colors border border-transparent hover:border-border/50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="p-2 rounded-lg bg-foreground/5 shrink-0">
                        <item.icon className="h-5 w-5 text-foreground" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{item.name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{item.description}</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
                    </Link>
                  ))}
                </div>
              </div>

              <hr className="border-border/50" />

              {/* General Links */}
              <div className="grid grid-cols-1 gap-2">
                <Link
                  href="/pricing"
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted transition-colors border border-transparent hover:border-border/50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="p-2 rounded-lg bg-foreground/5 shrink-0">
                    <Coins className="h-5 w-5 text-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{t("pricing")}</div>
                    <div className="text-xs text-muted-foreground">Nos offres et tarifs</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
                </Link>

                <Link
                  href="/careers"
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted transition-colors border border-transparent hover:border-border/50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="p-2 rounded-lg bg-foreground/5 shrink-0">
                    <Briefcase className="h-5 w-5 text-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{t("careers")}</div>
                    <div className="text-xs text-muted-foreground">Rejoignez l'aventure</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
                </Link>

                <Link
                  href="/contact"
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted transition-colors border border-transparent hover:border-border/50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="p-2 rounded-lg bg-foreground/5 shrink-0">
                    <MessageSquare className="h-5 w-5 text-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{t("contact")}</div>
                    <div className="text-xs text-muted-foreground">Une question ? Écrivez-nous</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
                </Link>
              </div>

              <hr className="border-border/50" />

              {/* Preferences: Language & Theme */}
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-foreground/5 shrink-0">
                      <Globe className="h-5 w-5 text-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{t("language")}</div>
                      <div className="text-xs text-muted-foreground">Changer de langue</div>
                    </div>
                  </div>
                  <div className="flex gap-1 bg-muted p-1 rounded-lg">
                    <Button
                      variant={locale === "fr" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleLanguageChange("fr")}
                      className={cn("h-7 px-3 text-xs", locale === "fr" && "bg-background shadow-sm hover:bg-background")}
                    >
                      FR
                    </Button>
                    <Button
                      variant={locale === "en" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleLanguageChange("en")}
                      className={cn("h-7 px-3 text-xs", locale === "en" && "bg-background shadow-sm hover:bg-background")}
                    >
                      EN
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-foreground/5 shrink-0">
                      <Palette className="h-5 w-5 text-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{t("theme")}</div>
                      <div className="text-xs text-muted-foreground">{t("theme_description")}</div>
                    </div>
                  </div>
                  <ThemeToggle className="h-9 w-9 border shadow-sm" />
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-6 pb-20">
                {!isConnected ? (
                  <Button asChild size="lg" className="w-full rounded-full bg-primary text-primary-foreground font-semibold">
                    <Link href={authUrl} onClick={() => setIsMobileMenuOpen(false)}>
                      {t("login")}
                    </Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="outline" size="lg" className="w-full rounded-full border-2 font-semibold">
                      <Link href={accountUrl} onClick={() => setIsMobileMenuOpen(false)}>
                        <User className="mr-2 h-5 w-5" />
                        {t("my_account")}
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="lg"
                      className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 font-medium"
                      onClick={() => {
                        handleSignOut()
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      {t("logout")}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header >
  )
}
